import type { LinkProps } from "@tanstack/react-router";
import type { MessageKey } from "../i18n.ts";

/*
 * «Операції» раздела: рабочие экраны и списки документов, которые в Odoo
 * стоят за рабочими местами (handoff 3 ноября). На ПК подпункты раскрываются
 * под активным разделом в колонке, на телефоне в шторке, и все они есть в
 * палитре Ctrl+K. Право на пункт только прячет его, проверяет сервер.
 *
 * Отдельно от sections.ts: разделы правят несколько потоков, а операции
 * модуль добавляет своей строкой ниже, не трогая чужие.
 */
export interface Operation {
  id: string;
  label: MessageKey;
  /** Путь по схеме /<раздел>/<документ>; компилятор сверяет его с маршрутами. */
  to: NonNullable<LinkProps["to"]>;
  permission?: string;
}

export interface SectionOperations {
  /** Пути раздела: по ним колонка понимает, какой раздел открыт. */
  prefixes: string[];
  items: Operation[];
}

export const OPERATIONS: Partial<Record<string, SectionOperations>> = {
  stock: {
    prefixes: ["/stock"],
    items: [
      { id: "hub", label: "ops.stock.hub", to: "/stock" },
      { id: "shelf", label: "ops.stock.shelf", to: "/stock/shelf", permission: "inventory.operate" },
      { id: "today", label: "ops.stock.today", to: "/stock/today", permission: "inventory.operate" },
      { id: "requests", label: "ops.stock.requests", to: "/stock/requests" },
    ],
  },
  catalog: {
    prefixes: ["/catalog"],
    items: [
      { id: "products", label: "ops.catalog.products", to: "/catalog/products" },
      { id: "templates", label: "ops.catalog.templates", to: "/catalog/templates" },
      { id: "versions", label: "ops.catalog.versions", to: "/catalog/versions" },
      { id: "firmware", label: "ops.catalog.firmware", to: "/catalog/firmware" },
      { id: "skills", label: "ops.catalog.skills", to: "/catalog/skills" },
    ],
  },
  purchase: {
    prefixes: ["/purchasing"],
    items: [{ id: "work", label: "ops.purchase.work", to: "/purchasing", permission: "purchasing.order" }],
  },
  mrp: {
    prefixes: ["/production/line", "/production/orders", "/production/boms"],
    items: [
      { id: "line", label: "ops.mrp.line", to: "/production/line" },
      { id: "orders", label: "ops.mrp.orders", to: "/production/orders" },
      { id: "new", label: "ops.mrp.new", to: "/production/orders/new", permission: "manufacturing.order" },
      { id: "boms", label: "ops.mrp.boms", to: "/production/boms" },
    ],
  },
  route: {
    prefixes: ["/production/my", "/production/cards"],
    items: [
      { id: "my", label: "ops.route.my", to: "/production/my", permission: "route.mark" },
      { id: "cards", label: "ops.route.cards", to: "/production/cards" },
    ],
  },
  sales: {
    prefixes: ["/sales"],
    items: [
      { id: "orders", label: "ops.sales.orders", to: "/sales/orders" },
      { id: "new", label: "ops.sales.new", to: "/sales/orders/new", permission: "sales.order" },
    ],
  },
  repair: {
    prefixes: ["/production/unit", "/service"],
    items: [
      { id: "unit", label: "ops.repair.unit", to: "/production/unit" },
      { id: "repairs", label: "ops.repair.repairs", to: "/service/repairs" },
      { id: "new", label: "ops.repair.new", to: "/service/repairs/new" },
      { id: "works", label: "ops.repair.works", to: "/service/works" },
      { id: "quarantine", label: "ops.repair.quarantine", to: "/service/quarantine" },
    ],
  },
  bank: {
    prefixes: ["/money/bank"],
    items: [
      { id: "work", label: "ops.bank.work", to: "/money/bank" },
      { id: "accounts", label: "ops.bank.accounts", to: "/money/bank/accounts" },
      { id: "payments", label: "ops.bank.payments", to: "/money/bank/payments" },
      { id: "statements", label: "ops.bank.statements", to: "/money/bank/statements" },
    ],
  },
  expense: {
    prefixes: ["/money/opex"],
    items: [
      { id: "work", label: "ops.expense.work", to: "/money/opex" },
      { id: "entries", label: "ops.expense.entries", to: "/money/opex/entries" },
      { id: "articles", label: "ops.expense.articles", to: "/money/opex/articles" },
      { id: "rules", label: "ops.expense.rules", to: "/money/opex/rules" },
      { id: "templates", label: "ops.expense.templates", to: "/money/opex/templates" },
    ],
  },
  contacts: {
    prefixes: ["/partners"],
    items: [{ id: "partners", label: "ops.contacts.partners", to: "/partners" }],
  },
  dashboards: {
    prefixes: ["/costing"],
    items: [
      { id: "close", label: "costing.close.title", to: "/costing/close" },
      { id: "pnl", label: "costing.pnl.title", to: "/costing/pnl" },
      { id: "prices", label: "costing.prices.title", to: "/costing/prices" },
    ],
  },
};

/** Операции раздела, видимые пользователю. */
export const visibleOperations = (section: string, can: (permission: string) => boolean): Operation[] =>
  OPERATIONS[section]?.items.filter((o) => !o.permission || can(o.permission)) ?? [];

/** Раздел открыт, если путь под одним из его префиксов. */
export const sectionActive = (section: string, pathname: string): boolean =>
  OPERATIONS[section]?.prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`)) ?? false;
